import { routes, locales, defaultLocale } from '../locales/routes.json';
import type { RouteKey as ImportedRouteKey } from '../types/routes';

type Locale = (typeof locales)[number];
type RouteKey = ImportedRouteKey | 'homepage';

// Fallback locale for missing route translations (English first, then defaultLocale)
const ROUTE_FALLBACK_LOCALE = locales.includes('en') ? 'en' : defaultLocale;

/**
 * Creates a locale-aware route path builder.
 * @param locale - The current locale
 * @param channelDefaultLocale - Optional default locale from channel config (for hostname-based routing).
 *                               When provided, this locale will have no prefix instead of the global default.
 */
export function createRoutePath(locale: Locale, channelDefaultLocale?: string) {
  const effectiveDefault = channelDefaultLocale ?? defaultLocale;

  return function routePath(key: RouteKey, trailing = ''): string {
    if (key === 'homepage') {
      return locale === effectiveDefault ? '/' : `/${locale}`;
    }

    // Try to get route translation for current locale
    const localeRoutes = routes[locale as keyof typeof routes];
    let translated = localeRoutes?.[key as keyof typeof localeRoutes];

    // Fallback to English or defaultLocale if translation is missing
    if (!translated) {
      const fallbackRoutes = routes[ROUTE_FALLBACK_LOCALE as keyof typeof routes];
      translated = fallbackRoutes?.[key as keyof typeof fallbackRoutes];

      if (!translated) {
        console.warn(
          `[routePath] Missing route translation for "${key}" in locale "${locale}" and fallback "${ROUTE_FALLBACK_LOCALE}"`
        );
        // Last resort: use the key itself as the route
        translated = key;
      } else {
        console.warn(`[routePath] Using fallback "${ROUTE_FALLBACK_LOCALE}" for route "${key}" in locale "${locale}"`);
      }
    }

    const suffix = trailing ? `/${trailing}` : '';

    if (locale === effectiveDefault) {
      return `/${translated}${suffix}`;
    }

    return `/${locale}/${translated}${suffix}`;
  };
}
